import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isProduction, getSiteEnv } from '@/lib/growthIntel/env';
import { isInternalVisitor, isTestVisitor } from '@/lib/growthIntel/tracker';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY ?? '';

/** Parses a rough device type from user-agent string */
function getDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk|(android(?!.*mobi))/.test(ua)) return 'tablet';
  if (/mobile|android|iphone|ipod|blackberry|windows phone|opera mini|iemobile/.test(ua)) return 'mobile';
  return 'desktop';
}

/** Parses a browser name from user-agent */
function getBrowser(): string {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\/|Opera/.test(ua)) return 'Opera';
  if (/SamsungBrowser/.test(ua)) return 'Samsung';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  if (/Trident\/|MSIE/.test(ua)) return 'IE';
  return 'Other';
}

/** Gets or creates a persistent session ID (expires after 30 min of inactivity) */
function getSessionId(): string {
  const SESSION_KEY = 'cendien_session_id';
  const EXPIRY_KEY = 'cendien_session_expiry';
  const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

  const now = Date.now();
  const expiry = parseInt(sessionStorage.getItem(EXPIRY_KEY) ?? '0', 10);
  let sessionId = sessionStorage.getItem(SESSION_KEY);

  if (!sessionId || now > expiry) {
    sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }

  // Always refresh expiry on activity
  sessionStorage.setItem(EXPIRY_KEY, String(now + SESSION_DURATION));
  return sessionId;
}

/** Returns true if this is the visitor's first-ever visit (uses localStorage) */
function isNewVisitor(): boolean {
  const key = 'cendien_returning';
  if (localStorage.getItem(key)) return false;
  localStorage.setItem(key, '1');
  return true;
}

/**
 * Tracks all public-page visits by calling the track-visitor edge function.
 * Admin routes are skipped automatically.
 * Captures: path, referrer, session ID, device type, browser, screen resolution, new-visitor flag.
 */
export function usePageTracker() {
  const location = useLocation();
  const lastTracked = useRef('');
  const pageEnterTime = useRef<number>(Date.now());

  useEffect(() => {
    const path = location.pathname;

    // Skip admin routes
    if (path.startsWith('/admin')) return;

    // HARD GUARD: never record traffic off the production host.
    if (!isProduction()) return;

    const isSamePage = path === lastTracked.current;

    // Record time on the previous page before updating
    const timeOnPage = isSamePage ? 0 : Math.round((Date.now() - pageEnterTime.current) / 1000);
    pageEnterTime.current = Date.now();

    // Skip repeated same-page calls (e.g. React StrictMode double-invoke)
    if (isSamePage) return;
    lastTracked.current = path;

    if (!SUPABASE_URL) return;

    const payload = {
      page: path,
      url: window.location.href,
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
      session_id: getSessionId(),
      device_type: getDeviceType(),
      browser: getBrowser(),
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      time_on_page: timeOnPage > 0 ? timeOnPage : null,
      is_new_visitor: isNewVisitor(),
      environment: getSiteEnv(),
      is_internal: isInternalVisitor(),
      is_test: isTestVisitor(),
    };

    // Fire-and-forget — never block rendering
    fetch(`${SUPABASE_URL}/functions/v1/track-visitor`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(payload),
    }).catch(() => { /* silently ignore tracking errors */ });
  }, [location.pathname]);
}
